/** Customer portal — track my company's ONE Orders with journey timeline. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { PackageSearch, ChevronDown, ChevronUp } from "lucide-react";
import { OrderTimeline } from "@/components/orders/FulfilmentPanel";

const TONE = {
  CREATED: "bg-slate-100 text-slate-700 border-slate-200",
  IN_FULFILLMENT: "bg-sky-50 text-sky-700 border-sky-200",
  READY_TO_SHIP: "bg-sky-100 text-sky-800 border-sky-200",
  SHIPPED: "bg-amber-50 text-amber-800 border-amber-200",
  DELIVERED: "bg-emerald-50 text-emerald-800 border-emerald-200",
  INVOICED: "bg-violet-50 text-violet-800 border-violet-200",
  CLOSED: "bg-slate-100 text-slate-600 border-slate-200",
  CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
};

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

export default function MyOrders() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/one-orders/portal/my-orders")
      .then(({ data }) => setRows(data.items || [])).catch(() => setRows([]));
  }, []);

  return (
    <div className="max-w-[900px] mx-auto space-y-4" data-testid="my-orders-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center"><PackageSearch className="h-5 w-5" /></div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Our Orders</h1>
          <p className="text-xs text-slate-500">Track every order's journey from placement to delivery</p>
        </div>
      </header>

      {rows === null && <div className="text-sm text-slate-400 p-6">Loading orders…</div>}
      {rows?.length === 0 && (
        <div className="p-10 text-center text-sm text-slate-400 bg-white border border-slate-200 rounded-md">
          No orders yet for your organisation.</div>)}

      <div className="space-y-2">
        {(rows || []).map((o) => (
          <div key={o.id} className="bg-white border border-slate-200 rounded-md" data-testid={`my-order-${o.code}`}>
            <button onClick={() => setOpen(open === o.id ? null : o.id)}
              className="w-full p-3 flex items-center gap-3 flex-wrap text-left"
              data-testid={`my-order-toggle-${o.code}`}>
              <span className="font-mono font-semibold text-slate-800">{o.code}</span>
              <Badge className={`text-[10px] border ${TONE[o.status] || ""}`}>{(o.status || "").replace(/_/g, " ")}</Badge>
              {o.customer_po_number && <span className="text-[11px] text-slate-500">PO {o.customer_po_number}</span>}
              <span className="text-[11px] text-slate-400 flex-1">
                {(o.items || []).length} item(s) · placed {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</span>
              <span className="text-[12px] font-mono">{fmtINR(o.grand_total_inr)}</span>
              {open === o.id ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {open === o.id && (
              <div className="px-4 pb-4 border-t border-slate-100">
                <div className="mt-3 text-[11px] text-slate-600 space-y-0.5">
                  {(o.items || []).map((it, i) => (
                    <div key={i}>• {it.product_name} × {it.quantity}</div>))}
                </div>
                <OrderTimeline order={o} />
              </div>)}
          </div>))}
      </div>
    </div>
  );
}
